// Copyright (c) 2026 Query Health Inc. All rights reserved.
// Author: Anmol Rajpal <anmol@quantinohealth.com>
// Licensed under a Commercial License. See LICENSE for terms.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronRight, ChevronLeft, Loader2, XCircle, CheckCircle2, X } from 'lucide-react'
import { createOrg, ApiError } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

// ── Exported validation helpers (unit-testable) ───────────────────────────────

const SLUG_RE = /^[a-z0-9-]{1,64}$/
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const DOMAIN_RE = /^[a-z0-9.-]+\.[a-z]{2,}$/

/** Returns true if slug matches ^[a-z0-9-]{1,64}$ */
export function validateSlug(slug: string): boolean {
  return SLUG_RE.test(slug)
}

/** Returns true if email has a basic valid format */
export function validateEmail(email: string): boolean {
  return EMAIL_RE.test(email)
}

/**
 * Parses a mixed comma/space/newline-separated domain string.
 * Lowercases, deduplicates, and filters to valid domain patterns.
 */
export function parseDomains(raw: string): string[] {
  const tokens = raw
    .split(/[\s,\n]+/)
    .map(t => t.trim().toLowerCase())
    .filter(Boolean)
  const valid = tokens.filter(t => DOMAIN_RE.test(t))
  return [...new Set(valid)]
}

/** Derives a URL-safe slug from an org display name */
function slugFromName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
}

// ── Wizard state ──────────────────────────────────────────────────────────────

type Step = 1 | 2 | 3 | 4 | 5

interface WizardData {
  displayName: string
  slug: string
  domainsRaw: string
  adminEmail: string
}

interface OnboardOrgWizardProps {
  onDone: () => void
}

export default function OnboardOrgWizard({ onDone }: OnboardOrgWizardProps) {
  const { t } = useTranslation()

  const [step, setStep] = useState<Step>(1)
  const [data, setData] = useState<WizardData>({
    displayName: '',
    slug: '',
    domainsRaw: '',
    adminEmail: '',
  })

  // Per-step validation errors
  const [slugError, setSlugError] = useState<string | null>(null)

  // Submit state
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  // ── Step helpers ──────────────────────────────────────────────────────────

  function canAdvanceStep(): boolean {
    switch (step) {
      case 1: return data.displayName.trim().length > 0
      case 2: return validateSlug(data.slug)
      case 3: return true // optional
      case 4: return validateEmail(data.adminEmail.trim())
      case 5: return true
    }
  }

  function handleNext() {
    if (step === 2) {
      if (!validateSlug(data.slug)) {
        setSlugError('onboard.slugInvalid')
        return
      }
      setSlugError(null)
    }
    if (step < 5) setStep((s => (s + 1) as Step)(step))
  }

  function handleBack() {
    if (step > 1) setStep((s => (s - 1) as Step)(step))
  }

  function handleDisplayNameChange(name: string) {
    setData(d => ({
      ...d,
      displayName: name,
      // Auto-suggest slug only if user hasn't manually edited it yet
      slug: d.slug === slugFromName(d.displayName) || d.slug === ''
        ? slugFromName(name)
        : d.slug,
    }))
  }

  async function handleSubmit() {
    setSubmitting(true)
    setSubmitError(null)
    const domains = parseDomains(data.domainsRaw)
    try {
      await createOrg({
        slug: data.slug.trim(),
        displayName: data.displayName.trim(),
        adminEmail: data.adminEmail.trim(),
        ...(domains.length > 0 ? { domains } : {}),
      })
      onDone()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'org_exists') {
        setSubmitError('onboard.slugExists')
      } else {
        setSubmitError('onboard.submitError')
      }
    } finally {
      setSubmitting(false)
    }
  }

  // ── Step renderers ────────────────────────────────────────────────────────

  function renderStepIndicator() {
    const steps = [1, 2, 3, 4, 5] as const
    return (
      <div className="flex items-center justify-center gap-2 mb-8">
        {steps.map(s => (
          <div
            key={s}
            className={[
              'h-2 rounded-full transition-all duration-200',
              s === step
                ? 'w-6 bg-primary'
                : s < step
                  ? 'w-2 bg-primary/50'
                  : 'w-2 bg-muted',
            ].join(' ')}
          />
        ))}
      </div>
    )
  }

  function renderStep() {
    switch (step) {
      case 1:
        return (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="display-name" className="text-sm font-medium text-foreground">
                {t('onboard.displayNameLabel')}
              </label>
              <p className="text-xs text-muted-foreground">{t('onboard.displayNameHint')}</p>
              <Input
                id="display-name"
                type="text"
                placeholder={t('onboard.displayNamePlaceholder')}
                value={data.displayName}
                onChange={e => handleDisplayNameChange(e.target.value)}
                aria-label={t('onboard.displayNameLabel')}
              />
            </div>
          </div>
        )

      case 2:
        return (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="slug" className="text-sm font-medium text-foreground">
                {t('onboard.slugLabel')}
              </label>
              <p className="text-xs text-muted-foreground">{t('onboard.slugHint')}</p>
              <Input
                id="slug"
                type="text"
                placeholder="acme-health"
                value={data.slug}
                onChange={e => {
                  const v = e.target.value
                  setData(d => ({ ...d, slug: v }))
                  // Show inline error as user types (only if non-empty and invalid)
                  if (v.length > 0 && !validateSlug(v)) {
                    setSlugError('onboard.slugInvalid')
                  } else {
                    setSlugError(null)
                  }
                }}
                aria-label={t('onboard.slugLabel')}
              />
              {slugError && (
                <p className="text-xs text-destructive">{t(slugError)}</p>
              )}
            </div>
          </div>
        )

      case 3: {
        const chips = parseDomains(data.domainsRaw)
        return (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="domains" className="text-sm font-medium text-foreground">
                {t('onboard.domainsLabel')}
              </label>
              <p className="text-xs text-muted-foreground">{t('onboard.domainsHint')}</p>
              <textarea
                id="domains"
                className="h-24 w-full rounded-xl border border-border bg-card px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                placeholder="acme.com, acme.org"
                value={data.domainsRaw}
                onChange={e => setData(d => ({ ...d, domainsRaw: e.target.value }))}
                aria-label={t('onboard.domainsLabel')}
              />
              {/* Domain chips */}
              {chips.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {chips.map(domain => (
                    <span
                      key={domain}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20"
                    >
                      {domain}
                      <button
                        type="button"
                        onClick={() => {
                          const remaining = chips.filter(d => d !== domain)
                          setData(d => ({ ...d, domainsRaw: remaining.join(', ') }))
                        }}
                        className="rounded-full hover:bg-primary/20 p-0.5 transition-colors"
                        aria-label={`Remove ${domain}`}
                      >
                        <X className="h-2.5 w-2.5" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        )
      }

      case 4:
        return (
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="admin-email" className="text-sm font-medium text-foreground">
                {t('onboard.adminEmailLabel')}
              </label>
              <p className="text-xs text-muted-foreground">{t('onboard.adminEmailHint')}</p>
              <Input
                id="admin-email"
                type="email"
                placeholder="admin@acme.com"
                value={data.adminEmail}
                onChange={e => setData(d => ({ ...d, adminEmail: e.target.value }))}
                aria-label={t('onboard.adminEmailLabel')}
              />
            </div>
          </div>
        )

      case 5: {
        const previewDomains = parseDomains(data.domainsRaw)
        return (
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold text-foreground">{t('onboard.review')}</h3>

            {/* Submit error */}
            {submitError && (
              <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-destructive/8 border border-destructive/20">
                <XCircle className="h-4 w-4 shrink-0 text-destructive" />
                <span className="text-sm text-destructive">{t(submitError)}</span>
              </div>
            )}

            <dl className="flex flex-col gap-3">
              {(
                [
                  ['onboard.reviewDisplayName', data.displayName],
                  ['onboard.reviewSlug', data.slug],
                  [
                    'onboard.reviewDomains',
                    previewDomains.length > 0 ? previewDomains.join(', ') : t('onboard.domainsNone'),
                  ],
                  ['onboard.reviewAdminEmail', data.adminEmail],
                ] as [string, string][]
              ).map(([labelKey, value]) => (
                <div key={labelKey} className="flex flex-col gap-0.5 bg-secondary/50 rounded-xl px-4 py-3">
                  <dt className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    {t(labelKey)}
                  </dt>
                  <dd className="text-sm text-foreground font-medium">{value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )
      }
    }
  }

  const isLast = step === 5

  return (
    <div className="max-w-lg mx-auto">
      <div className="bg-card border border-border rounded-3xl p-8 shadow-sm">
        {/* Step indicator */}
        {renderStepIndicator()}

        {/* Step title */}
        <div className="mb-6">
          <h2 className="text-xl font-bold text-foreground">
            {t(`onboard.step${step}Title`)}
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            {t('onboard.stepOf', { current: step, total: 5 })}
          </p>
        </div>

        {/* Step content */}
        {renderStep()}

        {/* Navigation */}
        <div className="flex gap-3 mt-8">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={submitting}
              className="flex-1"
            >
              <ChevronLeft className="h-4 w-4" />
              {t('onboard.back')}
            </Button>
          )}

          {isLast ? (
            <Button
              onClick={() => void handleSubmit()}
              disabled={submitting}
              className="flex-1"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('onboard.creating')}
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  {t('onboard.submit')}
                </>
              )}
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              disabled={!canAdvanceStep()}
              className="flex-1"
            >
              {t('onboard.next')}
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
